// API helpers for the courier ride flow (/:conneoId/rit/:rideId).
//
// Both calls go through the Cloudflare proxy worker. The proxy hides the
// internal ride endpoint (plain http + token) and queues the address status
// for AND_Flow's POST /api/processRideAddressDelivery. Expected proxy contract:
//   GET  {baseURL}/ride/{conneoId}/{rideId}        -> ride JSON ({ meta, data })
//   POST {baseURL}/ride/{conneoId}/{rideId}/event  -> queues { status, comment, signedby, deliveries }
// Override via .env.local (REACT_APP_RIDE_PROXY_BASE_URL) to point at a local `wrangler dev`
// instance of warehouse-picker-backend-worker instead of the deployed worker.
import { logRideEvent } from './rideTimeline';

const baseURL = process.env.REACT_APP_RIDE_PROXY_BASE_URL || "https://warehouse-picker-proxy.qustom.workers.dev/proxy";

export const rideUrl = (conneoId, rideId) => `${baseURL}/ride/${conneoId}/${rideId}`;
export const rideEventUrl = (conneoId, rideId) => `${rideUrl(conneoId, rideId)}/event`;

const readJson = async (response) => {
  if (!response.ok) {
    if (response.status === 403) throw new Error('Forbidden access! please provide a valid id');
    throw new Error(`Network response was not ok: ${response.status}`);
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.indexOf('application/json') === -1) {
    const text = await response.text();
    throw new Error(`Expected JSON, got: ${text}`);
  }
  return response.json();
};

// Fetch the ride. Resolves with the `data` object: { addresses, ride, carrier }.
export const fetchRide = async (conneoId, rideId, signal) => {
  const response = await fetch(rideUrl(conneoId, rideId), { signal });
  const json = await readJson(response);
  if (!json.meta || json.meta.status !== 'success' || !json.data) {
    throw new Error((json.meta && json.meta.message) || 'Rit kon niet worden geladen');
  }
  return json.data;
};

// Build the payload for one address: just its own status, not the whole ride.
export const buildAddressStatusPayload = ({ delivered, comment, signedby, deliveries }) => ({
  status: delivered ? 'Geleverd' : 'Onbestelbaar',
  comment,
  signedby,
  deliveries
});

// Post the address status. The worker queues it and answers immediately,
// so a 2xx only means "received". Callers must NOT refetch the ride after this.
//
// A thrown error carries `retryable`: true for network failures and 5xx (worth
// queuing for background retry), false for 4xx (the same payload will fail again
// no matter how often it's retried, e.g. a missing/invalid `deliveries` - see
// warehouse-picker-backend-worker's rideController.ts).
export const postAddressStatus = async (conneoId, rideId, payload) => {
  let response;
  try {
    response = await fetch(rideEventUrl(conneoId, rideId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch (err) {
    const error = new Error('Niet opgeslagen, server onbereikbaar');
    error.retryable = true;
    throw error;
  }
  if (!response.ok) {
    const error = new Error(`Fout bij verzenden van data (${response.status})`);
    error.retryable = response.status >= 500 || response.status === 429;
    throw error;
  }
  return true;
};

// Pure helper: returns a copy of one address with a delivery result applied.
const applyResultToAddress = (address, { delivered, comment, signedby, deliveries }) => {
  const base = {
    ...address,
    processed: true,
    delivered,
    undeliverable: !delivered,
    color: delivered ? '#7ED321' : '#F5A623',
    comment,
    signedby
  };
  return delivered
    ? { ...base, deliveries }
    : { ...base, selectedDeliveryItems: deliveries };
};

// Pure helper: returns a new ride data object with one address marked as
// delivered or undeliverable. Never mutates the input.
export const applyAddressResult = (rideData, addressKey, result) => ({
  ...rideData,
  addresses: rideData.addresses.map(address =>
    address.key === addressKey ? applyResultToAddress(address, result) : address
  )
});

// A payload as sent to postAddressStatus, reconstructed back into the
// {delivered, comment, signedby, deliveries} shape applyResultToAddress needs.
// buildAddressStatusPayload's mapping is exactly reversible: 'Geleverd' <-> delivered.
const resultFromPayload = (payload) => ({
  delivered: payload.status === 'Geleverd',
  comment: payload.comment,
  signedby: payload.signedby,
  deliveries: payload.deliveries
});

// Unprocessed addresses first, processed at the bottom; order otherwise preserved.
export const sortAddresses = (addresses = []) =>
  [...addresses].sort((a, b) => Number(!!a.processed) - Number(!!b.processed));

// Offline support: while a ride has addresses that failed to sync, its state
// (including those not-yet-synced local changes) is cached so a page refresh
// doesn't overwrite it with a fresh (and therefore stale, pre-change) server
// fetch. As soon as the pending queue empties again, the cache is dropped and
// a refresh goes back to fetching the ride from the server as normal.
//
// The cache is tagged with a version and a save time and re-validated on load:
// a shape mismatch (e.g. a leftover cache from an older app version, or a
// partial write) or an entry older than PENDING_CACHE_MAX_AGE_MS is discarded
// rather than trusted, so a stale/corrupt cache can't crash the render or keep
// masking the real server state (a courier reopening a days-old ride link
// should see the current server state, not an ancient local mutation).
const PENDING_CACHE_VERSION = 1;
const PENDING_CACHE_MAX_AGE_MS = 8 * 60 * 60 * 1000; // one workday

const pendingCacheKey = (conneoId, rideId) => `ride-pending:${conneoId}:${rideId}`;

const isValidPendingCache = (cached) =>
  !!cached &&
  cached.version === PENDING_CACHE_VERSION &&
  typeof cached.savedAt === 'number' &&
  Date.now() - cached.savedAt < PENDING_CACHE_MAX_AGE_MS &&
  !!cached.ride && Array.isArray(cached.ride.addresses) &&
  Array.isArray(cached.pendingQueue);

export const loadPendingRide = (conneoId, rideId) => {
  const key = pendingCacheKey(conneoId, rideId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    if (!isValidPendingCache(cached)) {
      localStorage.removeItem(key);
      return null;
    }
    return cached;
  } catch (err) {
    localStorage.removeItem(key);
    return null;
  }
};

const RIDE_CONFIRMED_PREFIX = 'ride-confirmed:';
const RIDE_PENDING_PREFIX = 'ride-pending:';

// Splits a `ride-pending:{conneoId}:{rideId}` key back into its parts.
// rideId is a GUID (no colons), but split on the first colon only, just in case.
const parsePendingKey = (storageKey) => {
  const rest = storageKey.slice(RIDE_PENDING_PREFIX.length);
  const sep = rest.indexOf(':');
  if (sep === -1) return null;
  return { conneoId: rest.slice(0, sep), rideId: rest.slice(sep + 1) };
};

// Called once per ride load. A driver is very unlikely to rescan an old QR
// code just to reopen a previous ride, so without this, anything still
// unsynced from an earlier ride when the driver moved on would only ever get
// another chance if that exact ride page happened to be reopened - otherwise
// it just sits in localStorage forever, both losing the delivery confirmation
// and slowly filling up the phone's storage. So opening a new ride also tries
// to flush every OTHER ride's still-pending items in the background (fire and
// forget - never blocks or throws into the caller, and never touches the
// ride currently being opened).
//
// ride-confirmed entries for other rides are always safe to drop outright:
// they're purely redundant once written (either already synced server-side,
// or just a 15-minute anti-flicker safety net for a ride nobody is looking at
// anymore).
export const flushOtherRideCaches = async (conneoId, rideId) => {
  const keepPending = pendingCacheKey(conneoId, rideId);
  const keepConfirmed = confirmedCacheKey(conneoId, rideId);
  let keys;
  try {
    keys = [];
    for (let i = 0; i < localStorage.length; i++) keys.push(localStorage.key(i));
  } catch (err) {
    return;
  }

  for (const storageKey of keys) {
    if (!storageKey) continue;

    if (storageKey.startsWith(RIDE_CONFIRMED_PREFIX) && storageKey !== keepConfirmed) {
      try { localStorage.removeItem(storageKey); } catch (err) { /* best effort */ }
      continue;
    }

    if (!storageKey.startsWith(RIDE_PENDING_PREFIX) || storageKey === keepPending) continue;

    const other = parsePendingKey(storageKey);
    let cached;
    try {
      cached = JSON.parse(localStorage.getItem(storageKey));
    } catch (err) {
      cached = null;
    }
    if (!other || !isValidPendingCache(cached)) {
      try { localStorage.removeItem(storageKey); } catch (err) { /* best effort */ }
      continue;
    }

    const stillPending = [];
    for (const item of cached.pendingQueue) {
      try {
        await postAddressStatus(other.conneoId, other.rideId, item.payload);
        addConfirmedAddressFromPayload(other.conneoId, other.rideId, item.addressKey, item.payload);
        logRideEvent(other.conneoId, other.rideId, {
          eventName: 'AddressSyncedFromNewerRide',
          eventMessage: `Adres alsnog gesynchroniseerd op de achtergrond, bij het openen van een volgende rit: ${item.addressLabel}`,
          functionName: 'flushOtherRideCaches',
          apiRequestUrl: rideEventUrl(other.conneoId, other.rideId),
          apiRequestContent: item.payload,
          extraData: { addressKey: item.addressKey, addressLabel: item.addressLabel, payload: item.payload }
        });
      } catch (err) {
        if (err.retryable) {
          stillPending.push(item);
        } else {
          // Permanent failure: no page is open to show this, so the timeline
          // is the only place it'll ever surface - trace it on that ride.
          logRideEvent(other.conneoId, other.rideId, {
            eventName: 'AddressSyncFailedPermanently',
            eventType: 'Error',
            eventMessage: err.message,
            functionName: 'flushOtherRideCaches',
            apiRequestUrl: rideEventUrl(other.conneoId, other.rideId),
            apiRequestContent: item.payload,
            extraData: { addressKey: item.addressKey, addressLabel: item.addressLabel, payload: item.payload, error: err.message }
          });
        }
      }
    }

    try {
      if (stillPending.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify({ ...cached, savedAt: Date.now(), pendingQueue: stillPending }));
      }
    } catch (err) {
      // best effort
    }
  }
};

export const savePendingRide = (conneoId, rideId, ride, pendingQueue) => {
  try {
    if (pendingQueue.length === 0) {
      localStorage.removeItem(pendingCacheKey(conneoId, rideId));
    } else {
      localStorage.setItem(pendingCacheKey(conneoId, rideId), JSON.stringify({
        version: PENDING_CACHE_VERSION,
        savedAt: Date.now(),
        ride,
        pendingQueue
      }));
    }
  } catch (err) {
    // localStorage can be unavailable or full; the queue then only survives in memory.
  }
};

// "Recently confirmed" cache: addresses whose delivery result was accepted by
// the server (a real success from postAddressStatus - never for pending/failed
// submissions, those already have their own representation via pendingQueue/
// submitError) but that a fresh GET might not reflect yet, since AND_Flow
// processes the queue asynchronously (usually seconds, occasionally longer
// under retry/backlog). Without this, a hard refresh briefly "undoes" an
// already-successful delivery in the UI until the backend catches up.
//
// Self-clearing: mergeConfirmedRide drops an entry the moment a fresh fetch
// shows the server agrees. CONFIRMED_CACHE_MAX_AGE_MS is only a safety net for
// the rare case the backend never catches up, so a genuine problem isn't
// masked forever - after that, the server's answer is trusted again.
const CONFIRMED_CACHE_VERSION = 1;
const CONFIRMED_CACHE_MAX_AGE_MS = 15 * 60 * 1000;

const confirmedCacheKey = (conneoId, rideId) => `ride-confirmed:${conneoId}:${rideId}`;

const isValidConfirmedCache = (cached) =>
  !!cached && cached.version === CONFIRMED_CACHE_VERSION && Array.isArray(cached.entries);

const saveConfirmedAddresses = (conneoId, rideId, entries) => {
  try {
    const key = confirmedCacheKey(conneoId, rideId);
    if (entries.length === 0) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify({ version: CONFIRMED_CACHE_VERSION, entries }));
    }
  } catch (err) {
    // best effort
  }
};

const loadConfirmedAddresses = (conneoId, rideId) => {
  const key = confirmedCacheKey(conneoId, rideId);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const cached = JSON.parse(raw);
    if (!isValidConfirmedCache(cached)) {
      localStorage.removeItem(key);
      return [];
    }
    const now = Date.now();
    const fresh = cached.entries.filter(entry =>
      typeof entry.confirmedAt === 'number' && now - entry.confirmedAt < CONFIRMED_CACHE_MAX_AGE_MS
    );
    if (fresh.length !== cached.entries.length) {
      saveConfirmedAddresses(conneoId, rideId, fresh);
    }
    return fresh;
  } catch (err) {
    localStorage.removeItem(key);
    return [];
  }
};

// Call right after a delivery result is successfully accepted by the server.
export const addConfirmedAddress = (conneoId, rideId, addressKey, result) => {
  const entries = loadConfirmedAddresses(conneoId, rideId).filter(entry => entry.addressKey !== addressKey);
  entries.push({ addressKey, result, confirmedAt: Date.now() });
  saveConfirmedAddresses(conneoId, rideId, entries);
};

// Same, but from the raw payload shape used by the pendingQueue's background retry.
export const addConfirmedAddressFromPayload = (conneoId, rideId, addressKey, payload) =>
  addConfirmedAddress(conneoId, rideId, addressKey, resultFromPayload(payload));

// Re-applies any locally confirmed results the server hasn't caught up with yet
// onto freshly fetched ride data. Entries the server already agrees with are
// dropped immediately (self-clearing).
export const mergeConfirmedRide = (conneoId, rideId, rideData) => {
  const entries = loadConfirmedAddresses(conneoId, rideId);
  if (entries.length === 0 || !rideData || !Array.isArray(rideData.addresses)) return rideData;

  const stillUnconfirmed = [];
  const merged = {
    ...rideData,
    addresses: rideData.addresses.map(address => {
      const entry = entries.find(e => e.addressKey === address.key);
      if (!entry) return address;
      if (address.processed) return address; // server has caught up
      stillUnconfirmed.push(entry);
      return applyResultToAddress(address, entry.result);
    })
  };

  saveConfirmedAddresses(conneoId, rideId, stillUnconfirmed);
  return merged;
};
